import { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ServerRail from './ServerRail';
import Sidebar from './Sidebar';
import MemberPanel from './MemberPanel';
import ServerSidebar from '../servers/ServerSidebar';
import ServerMembers from '../servers/ServerMembers';
import { lastChannel } from '../servers/format';
import { LayoutContext, useIsDesktop, useIsWide, useSwipe } from './hooks';
import { springs, suppressExits, useMotion } from '../motion';

// How deep a screen sits in the navigation (iOS style): going deeper pushes in from the right,
// going back slides the screen away to the right
const isListPath = (path) => path === '/dm' || /^\/servers\/[a-z0-9]{10}$/.test(path);
const depthOf = (path) => (isListPath(path) ? 0 : /^\/(profile|edit-profile)/.test(path) ? 2 : 1);

// Last opened conversation id (since Phase 3; the old 'last_chat' key held user ids)
const LAST_CHAT_KEY = 'last_conversation';

// App shell in Discord style.
// Desktop: server rail | DM sidebar or channel list | main | profile or member panel (≥1200px in a chat, otherwise as overlay).
// Mobile: list view (rail + sidebar) or main view; swipe right goes back to the list,
// swipe left opens the last chat or, inside a chat, the profile panel.
// Safe areas (notch, rounded corners, home indicator) come from the --safe-* variables in index.css.
export default function AppLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const isWide = useIsWide();
  const conversationId = parseInt(location.pathname.match(/^\/dm\/(\d+)$/)?.[1], 10) || null;
  const draftUserId = parseInt(location.pathname.match(/^\/dm\/user\/(\d+)$/)?.[1], 10) || null;
  const chatId = conversationId || draftUserId;
  // Community servers: /servers/:nodeId (channel list) and /servers/:nodeId/:channelId
  const serverMatch = location.pathname.match(/^\/servers\/([a-z0-9]{10})(?:\/(\d+))?$/);
  const serverId = serverMatch?.[1] || null;
  const channelId = Number(serverMatch?.[2]) || null;
  const inChat = !!(chatId || channelId);
  const isListRoute = isListPath(location.pathname);
  const [panelOpen, setPanelOpen] = useState(false);
  const swipeRef = useRef(null);

  // Direction of the current page change. Set while rendering, so the old page's exit (which
  // runs during the commit) already knows it. Other exits are skipped: the whole screen moves.
  const nav = useRef({ path: location.pathname, back: false, initial: true });
  if (nav.current.path !== location.pathname) {
    nav.current = { path: location.pathname, back: depthOf(location.pathname) < depthOf(nav.current.path), initial: false };
    suppressExits();
  }

  useEffect(() => { setPanelOpen(false); }, [location.pathname]);

  // The shell is fixed to the screen; the document itself must not scroll or bounce,
  // otherwise iOS shifts everything up under the status bar and shows a gap at the bottom
  useEffect(() => {
    document.documentElement.classList.add('shell-locked');
    return () => document.documentElement.classList.remove('shell-locked');
  }, []);
  useEffect(() => {
    if (conversationId) { try { localStorage.setItem(LAST_CHAT_KEY, String(conversationId)); } catch {} }
  }, [conversationId]);

  useSwipe(swipeRef, {
    onRight: () => {
      if (isDesktop) return;
      if (panelOpen) setPanelOpen(false);
      else if (!isListRoute) navigate(serverId ? `/servers/${serverId}` : '/dm');
    },
    onLeft: () => {
      if (isDesktop) return;
      if (isListRoute && serverId) {
        const last = lastChannel(serverId);
        if (last) navigate(`/servers/${serverId}/${last}`);
      } else if (isListRoute) {
        let last = null;
        try { last = localStorage.getItem(LAST_CHAT_KEY); } catch {}
        if (last) navigate(`/dm/${last}`);
      } else if (inChat) {
        setPanelOpen(true);
      }
    },
  });

  const layout = useMemo(() => ({
    isDesktop,
    panelOpen,
    openPanel: () => setPanelOpen(true),
    closePanel: () => setPanelOpen(false),
    togglePanel: () => setPanelOpen(v => !v),
  }), [isDesktop, panelOpen]);

  // Chats fill the main area; other pages keep their mobile-first width
  const content = inChat ? <div className="h-full">{children}</div> : <div className="mx-auto h-full max-w-[680px]">{children}</div>;
  const sidebar = serverId ? <ServerSidebar nodeId={serverId} /> : <Sidebar />;
  const panel = (onClose) => (channelId
    ? <ServerMembers nodeId={serverId} onClose={onClose} />
    : <MemberPanel conversationId={conversationId} userId={draftUserId} onClose={onClose} />);
  const overlayPanel = inChat && panelOpen && !(isDesktop && isWide) && (
    <PanelOverlay onClose={() => setPanelOpen(false)}>
      {panel(() => setPanelOpen(false))}
    </PanelOverlay>
  );

  if (isDesktop) {
    return (
      <LayoutContext.Provider value={layout}>
        <div ref={swipeRef} className="fixed inset-0 flex overflow-hidden bg-black pt-[var(--safe-top)] pl-[var(--safe-left)] pr-[var(--safe-right)]">
          <ServerRail />
          {sidebar}
          <main className="relative min-w-0 flex-1 overflow-y-auto overscroll-contain">
            <DesktopPage key={location.pathname}>{content}</DesktopPage>
          </main>
          {inChat && isWide && panel()}
          {overlayPanel}
        </div>
      </LayoutContext.Provider>
    );
  }

  return (
    <LayoutContext.Provider value={layout}>
      <div ref={swipeRef} className="fixed inset-0 overflow-hidden bg-black pt-[var(--safe-top)] pl-[var(--safe-left)] pr-[var(--safe-right)]">
        {isListRoute ? (
          <MobilePage key="list" nav={nav} shell={swipeRef} className="flex">
            <ServerRail />
            {sidebar}
          </MobilePage>
        ) : (
          <MobilePage key={location.pathname} nav={nav} shell={swipeRef} main className="overflow-y-auto overscroll-contain">{content}</MobilePage>
        )}
        {/* Inside the swipe area, so swiping right also closes the panel */}
        {overlayPanel}
      </div>
    </LayoutContext.Provider>
  );
}

// Phone screen: pushed in from the right when going deeper, the old one slides a little to the left
// and darkens below it. Going back reverses this. The first screen after loading just appears.
function MobilePage({ nav, shell, main, className, children }) {
  const ref = useRef();
  const back = () => nav.current.back;
  useMotion(ref, {
    enter: nav.current.initial ? undefined : nav.current.back ? { x: '-30%' } : { x: '100%' },
    exit: () => (back() ? { x: '100%' } : { x: '-30%', opacity: 0.4 }),
    ghost: () => ({ parent: shell.current, below: !back(), zIndex: 'auto' }),
    config: springs.page,
    exitConfig: springs.page,
    force: true,
  });
  const Tag = main ? 'main' : 'div';
  return (
    <Tag ref={ref} className={`relative h-full bg-black ${className}`} style={{ boxShadow: '-12px 0 32px rgba(0,0,0,0.45)' }}>
      {children}
    </Tag>
  );
}

// Desktop: the new content fades in and rises slightly
function DesktopPage({ children }) {
  const ref = useRef();
  useMotion(ref, { enter: { y: 8, opacity: 0 }, config: springs.snappy });
  return <div ref={ref} className="h-full">{children}</div>;
}

// Profile/members panel over the chat (phones, narrower desktops): slides in from the right
function PanelOverlay({ onClose, children }) {
  const ref = useRef();
  useMotion(ref, {
    parts: {
      '[data-backdrop]': { enter: { opacity: 0 }, exit: { opacity: 0 } },
      '[data-panel]': { enter: { x: '100%' }, exit: { x: '100%' } },
    },
    config: springs.sheet,
  });
  return (
    <div ref={ref} className="fixed inset-0 z-[400] flex justify-end" onClick={onClose}>
      <div data-backdrop className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.6)' }} />
      <div data-panel className="relative h-full" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  );
}
